/**
 * Event Form Handler for Dashboard
 * Handles event creation form submission
 */

import { FormSubmissionHandler } from '../utils/formSubmission.js'
import { ModalManager } from '../utils/modalManager.js'

export class EventFormHandler {
  constructor(form, modal) {
    this.form = form
    this.modal = modal
    this.submissionHandler = null
  }

  init() {
    if (!this.form) return

    // Remove any existing handler first to allow re-initialization
    if (this.form._formSubmissionHandler) {
      this.form.removeEventListener('submit', this.form._formSubmissionHandler, true)
      this.form.removeEventListener('submit', this.form._formSubmissionHandler, false)
    }
    
    // Reset the initialization flag to allow re-initialization
    // This ensures the handler is always ready when modal opens
    this.form.dataset.customHandlerInitialized = 'true'

    // Disable Rails UJS and Turbo to prevent conflicts
    // Remove data-remote and data-type attributes that trigger Rails UJS
    // Rails UJS checks for the presence of data-remote, so removing it prevents interception
    this.form.removeAttribute('data-remote')
    this.form.removeAttribute('data-type')
    this.form.setAttribute('data-turbo', 'false')
    // Add onsubmit to prevent default form submission as a backup
    this.form.onsubmit = (e) => {
      e.preventDefault()
      e.stopPropagation()
      return false
    }
    // Note: We keep local: false in the form_with helper to allow AJAX
    // but our custom handler will intercept before Rails UJS can process it
    
    // Ensure form action is set
    if (!this.form.action) {
      this.form.action = '/events'
    }
    this.form.method = 'POST'

    // CRITICAL: Reset any stuck states from previous submission attempts
    // This ensures the form is always in a clean state when initialized
    this.resetStuckState()

    this.submissionHandler = new FormSubmissionHandler(this.form, {
      submitUrl: '/events',
      method: 'POST',
      timeout: 30000,
      resetOnSuccess: false,
      onSuccess: (data) => this.handleSuccess(data),
      onError: (error) => this.handleError(error),
    })

    this.submissionHandler.init()
  }

  resetStuckState() {
    // Clear form submitting flag
    if (this.form) {
      this.form.dataset.submitting = 'false'
    }

    // Reset button state if it's stuck
    const submitBtn = this.form?.querySelector(
      'input[type="submit"], button[type="submit"]'
    )

    if (submitBtn) {
      // Check if button is stuck in loading state
      const currentText =
        submitBtn.tagName === 'INPUT' ? submitBtn.value : submitBtn.textContent

      if (currentText === 'Creating...' || submitBtn.disabled) {
        submitBtn.disabled = false

        // Reset to default text - check data-disable-with attribute first
        const defaultText =
          submitBtn.getAttribute('data-disable-with') || 'Create Event'
        if (submitBtn.tagName === 'INPUT') {
          submitBtn.value = defaultText
        } else {
          submitBtn.textContent = defaultText
        }
      }
    }
  }

  handleSuccess(data) {
    // CRITICAL: Clear form submitting flag IMMEDIATELY and SYNCHRONOUSLY
    // This must happen before anything else to prevent stuck states
    if (this.form) {
      this.form.dataset.submitting = 'false'
    }

    // Get button reference BEFORE doing anything else
    const submitBtn = this.form?.querySelector(
      'input[type="submit"], button[type="submit"]'
    )

    // CRITICAL: Reset button state IMMEDIATELY and SYNCHRONOUSLY
    // Do this before any async operations or DOM changes
    if (submitBtn) {
      submitBtn.disabled = false
      if (submitBtn.tagName === 'INPUT') {
        submitBtn.value = 'Create Event'
      } else {
        submitBtn.textContent = 'Create Event'
      }
    }

    // Reset form so it's ready for next use
    if (this.form) {
      this.form.reset()
    }

    // Close modal immediately (synchronous)
    ModalManager.hide(this.modal)

    setTimeout(() => {
      const redirectPath = this.successRedirectPath(data)
      const url = new URL(window.location.origin + redirectPath)
      url.searchParams.set('_t', Date.now().toString())
      window.location.href = url.toString()
    }, 200)
  }

  /**
   * Where a successful create lands.
   *
   * Ordinarily the form's own data-redirect-url — the events index or the
   * dashboard, whichever page rendered the modal — exactly as it always has.
   * This handler serves both, so it must never assume either.
   *
   * While an interactive tutorial run is active, the created event's editor
   * instead. Its "Save it" checkpoint says "this creates the event and opens its
   * quote", which was not true of any path through this handler
   * (docs/INTERACTIVE_TUTORIALS_PLAN.md §1.4). Making EVERY creation open the
   * editor is a real, arguably overdue improvement and an unrelated one — §1.4
   * option (b), flagged for its own go-ahead, not folded in here.
   *
   * Both conditions have to hold, and each fails safe on its own: no engine on
   * the page (the editor bundle, an older cached one, none at all) reads as no
   * run, and a payload without editor_url — a response from a deploy that
   * predates it — falls back to the redirect this method has always returned.
   */
  successRedirectPath(data) {
    const editorUrl = data && data.editor_url
    if (editorUrl && this.tourIsRunning()) return editorUrl

    return this.form?.dataset.redirectUrl || '/'
  }

  /**
   * Whether an interactive tutorial run is in progress.
   *
   * `?.` covers an absent engine but NOT a throwing one, and the difference
   * matters here more than it looks: this is called from inside the redirect
   * timer in handleSuccess, so a throw is swallowed by the timer and the
   * navigation simply never happens — the event IS created, the modal is
   * already hidden, and the florist is left on an unchanged page with no
   * feedback and no way to tell it worked. Creating an event must not be able
   * to fail because of the tutorial, which is the same rule tutorials/state.ts
   * already applies to every sessionStorage touch.
   */
  tourIsRunning() {
    try {
      return window.__blumeTour?.state()?.phase === 'run'
    } catch {
      return false
    }
  }

  handleError(error) {
    // Error handling is done by FormSubmissionHandler
    // Ensure form state is cleared on error
    console.error('EventFormHandler error:', error)
    if (this.form) {
      this.form.dataset.submitting = 'false'
    }
    
    // Reset button state
    const submitBtn = this.form?.querySelector(
      'input[type="submit"], button[type="submit"]'
    )
    if (submitBtn) {
      submitBtn.disabled = false
      if (submitBtn.tagName === 'INPUT') {
        submitBtn.value = 'Create Event'
      } else {
        submitBtn.textContent = 'Create Event'
      }
    }
  }
}
